import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useForm } from '../hooks';
import { usePageContext } from '../contexts';
import { submitApplication } from '../services/applicationService';
import {
  ApplicationProgressBar,
  ApplicationIntro,
  ApplicationSuccess,
  ApplicationError,
  DisqualificationView,
  ApplicationStep1,
  ApplicationStep2,
  ApplicationStep3,
  ApplicationStep4,
  ApplicationStep5,
  ApplicationStep6
} from '../pages/components';

export const ApplyPage = () => {
  const [step, setStep] = useState(0);
  const [isDisqualified, setIsDisqualified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);

  const { formData, handleInput } = useForm({
    linkedin: '',
    obsession: '',
    thesis: '',
    coldStart: '',
    email: '',
    phone: ''
  });

  const { navigateTo } = usePageContext();

  const nextStep = () => setStep(prev => prev + 1);
  const prevStep = () => setStep(prev => prev - 1);
  const handleDisqualification = () => setIsDisqualified(true);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmissionError(null);

    try {
      const result = await submitApplication(formData);

      if (result.success) {
        console.log('Application submitted successfully!', result.data);
        nextStep(); // Move to success screen
      } else {
        setSubmissionError(result.error);
        setStep(8); // Move to error screen
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      setSubmissionError('An unexpected error occurred. Please try again.');
      setStep(8); // Move to error screen
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = () => {
    setSubmissionError(null);
    setStep(6); // Go back to last step (contact info)
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white pt-32 pb-20 flex flex-col items-center justify-center relative overflow-hidden">
      <ApplicationProgressBar step={step} />
      <div className="w-full max-w-2xl px-6 relative z-10">
        {step > 0 && !isDisqualified && step < 7 && (
          <button
            onClick={prevStep}
            className="absolute -top-24 left-6 md:-left-12 text-zinc-600 hover:text-white transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
        )}
        <div className="animate-in fade-in slide-in-from-bottom-8 duration-500" key={step}>
          {step === 0 && <ApplicationIntro onNext={nextStep} />}

          {isDisqualified && <DisqualificationView />}

          {step === 1 && !isDisqualified && (
            <ApplicationStep1 onNext={nextStep} onDisqualify={handleDisqualification} />
          )}

          {step === 2 && !isDisqualified && (
            <ApplicationStep2 onNext={nextStep} formData={formData} handleInput={handleInput} />
          )}

          {step === 3 && !isDisqualified && (
            <ApplicationStep3 onNext={nextStep} formData={formData} handleInput={handleInput} />
          )}

          {step === 4 && !isDisqualified && (
            <ApplicationStep4 onNext={nextStep} formData={formData} handleInput={handleInput} />
          )}

          {step === 5 && !isDisqualified && (
            <ApplicationStep5 onNext={nextStep} formData={formData} handleInput={handleInput} />
          )}

          {step === 6 && !isDisqualified && (
            <ApplicationStep6
              onNext={handleSubmit}
              formData={formData}
              handleInput={handleInput}
              isSubmitting={isSubmitting}
            />
          )}

          {step === 7 && <ApplicationSuccess onHome={() => navigateTo('landing')} />}

          {step === 8 && <ApplicationError onRetry={handleRetry} error={submissionError} />}
        </div>
      </div>
    </div>
  );
};
